import { GTU_PRESET_KEYS } from '../sim/canvas/nodeSchemas/gasTurbineUnit';
import { RECIP_PRESET_KEYS } from '../sim/canvas/nodeSchemas/recipEngineBlock';
import { GENERATION_EQUIPMENT_CATALOG } from './generationEquipmentCatalog';
import {
  OEM_GENERATION_SOURCE_REGISTER,
  findOemGenerationProduct,
  oemGenerationProductRecordDigest,
} from './generationOemSourceRegister';

export type OemSimulationAdmissionStatus =
  | 'research-only'
  | 'comparison-only'
  | 'apply-approved';

export type OemSimulationConsumer = 'eo' | 'generation-impact';

export interface OemGenerationSimulationAdmission {
  id: string;
  productId: string;
  performanceCaseId: string;
  consumer: OemSimulationConsumer;
  status: Exclude<OemSimulationAdmissionStatus, 'research-only'>;
  targetId: string;
  targetComponent: 'gas-turbine-unit' | 'recip-engine-block' | 'generation-impact-envelope';
  reviewedAt: string;
  reviewedBy: string;
  modelVersion: string;
  productRecordDigest: string;
  sourceIds: readonly string[];
  normalization: string;
  limitations: readonly string[];
}

/**
 * Fail-closed admission manifest. Evidence-register rows not listed here stay
 * research-only and cannot select a solver preset by name similarity.
 */
export const OEM_GENERATION_SIMULATION_ADMISSIONS: readonly OemGenerationSimulationAdmission[] = [
  {
    id: 'eo-ge-9ha01-screening-v1',
    productId: 'ge-9ha01-simple-cycle-2025',
    performanceCaseId: '50hz-simple-cycle',
    consumer: 'eo',
    status: 'apply-approved',
    targetId: 'GE_9HA_F_heavy_duty',
    targetComponent: 'gas-turbine-unit',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic engineering source-to-preset review',
    modelVersion: 'gtu_presets.py:v1',
    productRecordDigest: '2fc7447addbc25c3dba639260b9a4b482767a124d663748218ed93947d4f1c19',
    sourceIds: ['ge-gas-power-catalog-2025'],
    normalization: 'Exact 9HA.01 family identity selects the existing GE 9HA.01 Brayton screening preset; the 448 MW OEM plant rating is retained in the BOM basis and is not used to retune pressure ratio, TIT, component efficiencies, or air flow.',
    limitations: [
      'Screening preset only: it is not calibrated to the current 448 MW net-plant rating and must not be presented as an OEM heat balance.',
      'Obtain project ambient corrections, fuel specification, losses, emissions, off-design curves, and validated generator/control models before client use.',
    ],
  },
  {
    id: 'eo-wartsila-18v50sg-screening-v1',
    productId: 'wartsila-18v50sg',
    performanceCaseId: '60hz-full-load',
    consumer: 'eo',
    status: 'apply-approved',
    targetId: 'wartsila_50sg',
    targetComponent: 'recip-engine-block',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic engineering source-to-preset review',
    modelVersion: 'recip_presets.py:v1',
    productRecordDigest: '3f8af60f540e4414e283eb8f1cea71dae533ea5a0abdb0271b7a82f2a6fbed4a',
    sourceIds: ['wartsila-50sg-current'],
    normalization: 'Exact 18V50SG identity selects the existing Wärtsilä 50SG screening preset. The preset keeps its approximate 18.3 MW nameplate and assumed lean-burn lambda; the OEM 18.875 MW case remains frozen in the BOM snapshot.',
    limitations: [
      'The backend rating and lean-burn lambda are approximate and do not reproduce the current OEM leaflet exactly.',
      'Replace assumed lambda and off-design data with the selected-project heat balance before design use.',
    ],
  },
  {
    id: 'eo-cat-g3520-2mw-screening-v1',
    productId: 'caterpillar-g3520-2mw-screening',
    performanceCaseId: '60hz-2mw-family',
    consumer: 'eo',
    status: 'apply-approved',
    targetId: 'caterpillar_g3520',
    targetComponent: 'recip-engine-block',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic engineering source-to-preset review',
    modelVersion: 'recip_presets.py:v1',
    productRecordDigest: 'e078f3510aa313ac07a710f3c11f1f1fcbd9b7f80eac74edd957cc01c7e555be',
    sourceIds: ['cat-g3520-fast-response-current'],
    normalization: 'Exact G3520 2.0 MW family member selects the existing Caterpillar G3520 screening preset; no unfamiliar Caterpillar model is mapped to it.',
    limitations: [
      'The backend electrical efficiency, exhaust temperature, lean-burn lambda, and part-load curve are approximate and require the exact selected rating sheet.',
      'Standby/critical-power duty must not be interpreted as continuous-duty capability without OEM confirmation.',
    ],
  },
  {
    id: 'impact-ge-7ha01-1x1-v1',
    productId: 'ge-7ha01-1x1-combined-cycle',
    performanceCaseId: '60hz-1x1-cc',
    consumer: 'generation-impact',
    status: 'apply-approved',
    targetId: 'ge-7ha01-1x1-cc-60hz',
    targetComponent: 'generation-impact-envelope',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic generation-duty review',
    modelVersion: 'generation-equipment-catalog:v1',
    productRecordDigest: '6feea8427fe4e8ad65ef298803fe486f383263000bd961a11e8e7b91f504f78b',
    sourceIds: ['ge-7ha-power-plants-2025', 'ge-gas-power-catalog-2025'],
    normalization: 'The immutable Generation & Impact v1 envelope conserves OEM ISO fuel input while converting the 438 MW LHV net-plant duty to its explicit HHV gross-output screening convention.',
    limitations: ['Planning derates, water factors, and land intensity remain non-OEM screening assumptions; installed footprint is not admitted.'],
  },
  {
    id: 'impact-siemens-scc6-5000f-comparison-v1',
    productId: 'siemens-scc6-5000f-1x1',
    performanceCaseId: '60hz-current-pdf',
    consumer: 'generation-impact',
    status: 'comparison-only',
    targetId: 'siemens-scc6-5000f-1x1-60hz',
    targetComponent: 'generation-impact-envelope',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic generation-duty review',
    modelVersion: 'generation-equipment-catalog:v1',
    productRecordDigest: '9e9c7145d045c7ed2c7d734a99457eb9df4abafc046444f08072d8755c83cee1',
    sourceIds: ['siemens-scc6-5000f-2025'],
    normalization: 'The current 391 MW source case is retained for comparison against the separately governed v1 planning envelope; no automatic application is permitted.',
    limitations: ['Reconcile gross/net scope, heating-value basis, and conflicting current ratings before approval.'],
  },
  {
    id: 'impact-wartsila-24x18v50sg-v1',
    productId: 'wartsila-24x18v50sg-island',
    performanceCaseId: '60hz-24-engine',
    consumer: 'generation-impact',
    status: 'apply-approved',
    targetId: 'wartsila-24x18v50sg-electrical-island-60hz',
    targetComponent: 'generation-impact-envelope',
    reviewedAt: '2026-07-20',
    reviewedBy: 'Deep Atomic generation-duty review',
    modelVersion: 'generation-equipment-catalog:v1',
    productRecordDigest: '62173e1e733a2a18f5f039fdf4aed7f6117fb6f38d3090b330dcdc5aedc5598c',
    sourceIds: ['wartsila-50sg-current'],
    normalization: 'Arithmetic 24 × 18.875 MW generator-terminal aggregation maps to the immutable electrical-island screening envelope; no installed-plant guarantee is inferred.',
    limitations: ['Shared auxiliaries, collection losses, common-mode outages, acoustic design, and installed footprint require project engineering.'],
  },
];

export interface OemSimulationAdmissionAssessment {
  status: OemSimulationAdmissionStatus;
  admissions: readonly OemGenerationSimulationAdmission[];
  reason: string;
}

export function assessOemSimulationAdmission(
  productId: string,
  performanceCaseId: string,
  consumer?: OemSimulationConsumer,
): OemSimulationAdmissionAssessment {
  const matching = OEM_GENERATION_SIMULATION_ADMISSIONS.filter(admission => (
    admission.productId === productId
      && admission.performanceCaseId === performanceCaseId
      && (consumer == null || admission.consumer === consumer)
  ));
  if (matching.length === 0) {
    return {
      status: 'research-only',
      admissions: [],
      reason: 'No exact, reviewed simulation admission exists for this OEM performance case.',
    };
  }
  const applyApproved = matching.filter(admission => admission.status === 'apply-approved');
  return {
    status: applyApproved.length > 0 ? 'apply-approved' : 'comparison-only',
    admissions: matching,
    reason: applyApproved.length > 0
      ? 'An exact reviewed admission exists; its stated normalization and limitations still apply.'
      : 'The case is available for comparison only and is not solver-applicable.',
  };
}

export function findApplyApprovedOemEoAdmission(args: {
  productId: string;
  performanceCaseId: string;
  productRecordDigest: string;
  targetComponent: 'gas-turbine-unit' | 'recip-engine-block';
}): OemGenerationSimulationAdmission | null {
  return OEM_GENERATION_SIMULATION_ADMISSIONS.find(admission => (
    admission.productId === args.productId
      && admission.performanceCaseId === args.performanceCaseId
      && admission.consumer === 'eo'
      && admission.status === 'apply-approved'
      && admission.targetComponent === args.targetComponent
      && admission.productRecordDigest === args.productRecordDigest
  )) ?? null;
}

export function validateOemSimulationAdmissions(): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const generationImpactIds = new Set(
    GENERATION_EQUIPMENT_CATALOG.map(entry => entry.catalogId),
  );
  for (const admission of OEM_GENERATION_SIMULATION_ADMISSIONS) {
    if (ids.has(admission.id)) errors.push(`duplicate OEM simulation admission ${admission.id}`);
    ids.add(admission.id);
    const product = findOemGenerationProduct(admission.productId);
    if (!product) {
      errors.push(`${admission.id} references unknown product ${admission.productId}`);
      continue;
    }
    const performance = product.performanceCases.find(
      item => item.id === admission.performanceCaseId,
    );
    if (!performance) {
      errors.push(`${admission.id} references unknown performance case ${admission.performanceCaseId}`);
    }
    const currentDigest = oemGenerationProductRecordDigest(
      product,
      OEM_GENERATION_SOURCE_REGISTER,
    );
    if (admission.productRecordDigest !== currentDigest) {
      errors.push(`${admission.id} product digest is stale; re-review before applying`);
    }
    const sourceIds = new Set(OEM_GENERATION_SOURCE_REGISTER.sources.map(source => source.id));
    if (admission.sourceIds.length === 0
      || admission.sourceIds.some(sourceId => !sourceIds.has(sourceId))) {
      errors.push(`${admission.id} has missing or unknown source ids`);
    }
    if (admission.consumer === 'eo') {
      const allowed = admission.targetComponent === 'gas-turbine-unit'
        ? GTU_PRESET_KEYS as readonly string[]
        : admission.targetComponent === 'recip-engine-block'
          ? RECIP_PRESET_KEYS as readonly string[]
          : [];
      if (!allowed.includes(admission.targetId)) {
        errors.push(`${admission.id} targets unsupported EO preset ${admission.targetId}`);
      }
    } else if (admission.targetComponent !== 'generation-impact-envelope'
      || !generationImpactIds.has(admission.targetId)) {
      errors.push(`${admission.id} targets unknown Generation & Impact envelope ${admission.targetId}`);
    }
    if (!admission.normalization.trim() || admission.limitations.length === 0) {
      errors.push(`${admission.id} requires normalization and limitations`);
    }
  }
  return errors;
}
